import type { ReactNode } from 'react';
import { BaseLedgerRow } from '../internal/BaseLedgerRow';
import { formatLedgerWhen, formatSitePlot } from '../format';
import { DeductionLead, Money } from '../primitives';
import { isCancelledStatus, statusWord } from '../statusTokens';
import type { LedgerCollapse, LedgerColumn } from '../types';
import { composeMetaLine } from './metaLine';
import { workCells, type SitePlot } from './workCells';

export interface DeductionLedgerRowProps {
  columns: readonly LedgerColumn[];
  collapse: LedgerCollapse;
  /** The deduction's description. */
  title: string;
  /** A `WorkStatus` where the screen has one, any status string otherwise. */
  status: string;
  reference?: string | null;
  createdBy?: string | null;
  /** ISO timestamp. */
  createdAt?: string | null;
  /**
   * Negative. Rendered red, in the same Value column as everything else.
   * Null when the viewer may not see money.
   */
  amount?: number | null;
  /**
   * What the deduction is attached to. A deduction can name a plot, a site
   * alone, or neither, and the cell is how that scope reads.
   */
  sitePlot?: SitePlot;
  /** Word used for the kind in the meta line and in aria. */
  kindLabel?: string;
  showValues?: boolean;
  hoverCard?: ReactNode;
  onClick?: () => void;
  'data-testid'?: string;
}

/** What the deduction is matters more than its approval state, so kind leads. */
function ariaLabel(
  title: string,
  kindLabel: string,
  status: string,
  reference: string | null | undefined
): string {
  return [title, kindLabel, statusWord(status), reference]
    .filter((part): part is string => part != null && part.length > 0)
    .join(', ');
}

/**
 * A deduction: red minus in the lead, negative red amount, no status dot and
 * no context menu. What it is matters more here than whether it is approved,
 * and the approval state is a hover-card fact.
 */
export const DeductionLedgerRow = ({
  columns,
  collapse,
  title,
  status,
  reference,
  createdBy,
  createdAt,
  amount,
  sitePlot,
  kindLabel = 'Deduction',
  showValues = true,
  hoverCard,
  onClick,
  'data-testid': dataTestId,
}: DeductionLedgerRowProps) => {
  const meta = composeMetaLine([
    reference,
    kindLabel,
    statusWord(status),
    createdBy,
    formatLedgerWhen(createdAt),
    sitePlot ? formatSitePlot(sitePlot.site, sitePlot.plot) : null,
  ]);

  return (
    <BaseLedgerRow
      columns={columns}
      collapse={collapse}
      lead={<DeductionLead />}
      title={title}
      cells={workCells({
        reference,
        bookedBy: createdBy,
        bookedAt: createdAt,
        sitePlot,
      })}
      value={
        <Money
          value={amount}
          color="error.main"
          strike={isCancelledStatus(status)}
        />
      }
      meta={meta}
      dimmed={isCancelledStatus(status)}
      showValues={showValues}
      hoverCard={hoverCard}
      onClick={onClick}
      aria-label={ariaLabel(title, kindLabel, status, reference)}
      data-status={status}
      data-testid={dataTestId}
    />
  );
};
